import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import { IfPermissionDirective } from '../../../../core/auth/permission.directive';
import {
  UiBackdropButton,
  UiButton,
  UiDialog,
  UiDialogOverlay,
  UiDialogTitle,
  UiInput,
  UiLabel,
  UiMenuItem,
} from '../../../../shared/ui';
import { ConversationsStore } from '../../data/conversations.store';
import { DirectoryApiService, type DirectoryEntry } from '../../data/directory-api.service';

/**
 * Centered modal picker for routing a conversation. Replaces the popover-on-the-chip
 * variant. Surfaces only what makes sense for the current state:
 *   - "Atribuir a mim" — hidden when the user is already the assignee
 *   - "Remover atribuição" — hidden when the conversation has no assignee
 *   - Members / Teams sections — hidden when the underlying list is empty after filtering
 *
 * Permission gating happens at the parent (the open button is wrapped in *appIfPermission),
 * with finer-grained gates here for sub-actions (handoff vs assign).
 */
@Component({
  selector: 'app-assignee-picker-modal',
  imports: [
    UiBackdropButton,
    UiButton,
    UiDialog,
    UiDialogOverlay,
    UiDialogTitle,
    UiInput,
    UiLabel,
    UiMenuItem,
    IfPermissionDirective,
  ],
  templateUrl: './assignee-picker-modal.html',
  styleUrl: './assignee-picker-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssigneePickerModalComponent {
  private readonly directory = inject(DirectoryApiService);
  private readonly store = inject(ConversationsStore);

  readonly closed = output<void>();

  protected readonly members = signal<readonly DirectoryEntry[]>([]);
  protected readonly teams = signal<readonly DirectoryEntry[]>([]);
  protected readonly query = signal('');

  protected readonly filteredMembers = computed(() => filterEntries(this.members(), this.query()));
  protected readonly filteredTeams = computed(() => filterEntries(this.teams(), this.query()));

  /** Hide "Atribuir a mim" when the current user already owns the conversation. */
  protected readonly canAssumeSelf = computed(() => !this.store.selectedConversation()?.assignedToMe);
  /** Show "Remover atribuição" only when there IS an assignee to remove. */
  protected readonly hasAssignee = computed(() => {
    const session = this.store.selectedConversation()?.session;
    return !!session && session.assigneeType !== 'unassigned';
  });
  protected readonly isAssignedToAgent = computed(() => this.store.selectedConversation()?.session?.assigneeType === 'agent');

  constructor() {
    this.directory.listMembers().subscribe((members) => this.members.set(members));
    this.directory.listTeams().subscribe((teams) => this.teams.set(teams));
  }

  setQuery(value: string): void {
    this.query.set(value.trim().toLowerCase());
  }

  assumeSelf(): void {
    this.store.assignToMe();
    this.closed.emit();
  }

  toMember(member: DirectoryEntry): void {
    this.store.assignToMember(member.id);
    this.closed.emit();
  }

  toTeam(team: DirectoryEntry): void {
    this.store.assignToTeam(team.id);
    this.closed.emit();
  }

  toAgent(): void {
    this.store.assignToAgent();
    this.closed.emit();
  }

  unassign(): void {
    this.store.unassign();
    this.closed.emit();
  }

  close(): void {
    this.closed.emit();
  }
}

function filterEntries(entries: readonly DirectoryEntry[], query: string): readonly DirectoryEntry[] {
  if (query.length === 0) {
    return entries;
  }
  return entries.filter((entry) => entry.name.toLowerCase().includes(query));
}
